import { useMemo } from 'react';
import { LuClock3 } from 'react-icons/lu';
import Card from '../../kit/card/Card';
import StatefulCard from '../../kit/card/StatefulCard';
import StatelessCard from '../../kit/card/StatelessCard';
import Text from '../../kit/text/Text';
import Button from '../../kit/button/Button';
import { formatDate, isTomorrow } from 'src/app/utils/date-time.utils';
import { VyduraReminder, VyduraSchedule } from 'src/types/vydura-reminder';
import { useNavigate } from 'react-router-dom';
import { AppRoutes } from 'src/types/app-routes.enum';
import { MigraineAttack } from 'src/types/migraine-attack.types';
import { isAfter } from 'date-fns';

type VyduraReminderCardProps = {
  reminder?: VyduraReminder;
  vyduraPreventiveDays: MigraineAttack[];
  vyduraSchedule?: VyduraSchedule;
};

const VyduraReminderCard = ({
  reminder,
  vyduraPreventiveDays,
  vyduraSchedule,
}: VyduraReminderCardProps) => {
  const navigate = useNavigate();
  const nextPreventiveDay = useMemo(
    () =>
      vyduraPreventiveDays.find((day) => isAfter(day.date!, new Date()))?.date,
    [vyduraPreventiveDays]
  );
  const nextDoseTime: string | undefined = useMemo(() => {
    if (nextPreventiveDay) {
      const reminderTime = reminder?.time
        ? `${reminder.time?.hours}:${reminder.time?.minutes} ${reminder.time?.period.toLowerCase()}`
        : '';
      const reminderDay = isTomorrow(nextPreventiveDay!)
        ? 'Tomorrow'
        : formatDate(nextPreventiveDay!, false);
      return `${reminderDay}${reminderTime ? ', ' : ''}${reminderTime}`;
    }
    return undefined;
  }, [reminder, nextPreventiveDay]);
  const onClick = () => navigate(AppRoutes.Reminder);

  return (
    <>
      {vyduraSchedule?.isActive && reminder ? (
        <Card
          title='Vydura intake & reminder'
          editText='Edit'
          isEditable
          onEdit={onClick}
          onCardClick={onClick}
        >
          <StatefulCard
            action='Next Dose'
            imageSource='images/vydura-reminder.svg'
            name='1 Vydura 75mg'
            notification={
              <Text
                style={{
                  fontSize: '14px',
                  color: 'var(--color-text-gray)',
                  display: 'flex',
                  alignItems: 'center',
                }}
              >
                <LuClock3 />
                &nbsp;&nbsp;{nextDoseTime}
              </Text>
            }
          />
        </Card>
      ) : (
        <Card title='Vydura intake & reminder' onCardClick={onClick}>
          <StatelessCard
            text='Add your Vydura intake schedule and reminder'
            imageSource='images/vudyra-add-reminder.svg'
            Button={
              <Button
                type='transparent'
                width='content-width'
                onClick={onClick}
                style={{ fontWeight: 500, padding: 0, height: 'fit-content' }}
              >
                +&nbsp;&nbsp;Add schedule
              </Button>
            }
          />
        </Card>
      )}
    </>
  );
};

export default VyduraReminderCard;
